import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import IngredientEditor, { IngredientTable, parseIngredients, serializeIngredients } from "../components/IngredientEditor.jsx";
import ProcedureEditor, { ProcedureSteps, parseSteps, serializeSteps } from "../components/ProcedureEditor.jsx";
import UnitConverter from "../components/UnitConverter.jsx";
import PhotoGallery from "../components/PhotoGallery.jsx";
import { useRecipeQuery, useUpdateRecipe, useDeleteRecipe } from "../hooks/useRecipesQuery.js";
import Button from "../components/ui/Button.jsx";
import Card from "../components/ui/Card.jsx";
import Badge from "../components/ui/Badge.jsx";
import Select from "../components/ui/Select.jsx";
import ErrorState from "../components/ui/ErrorState.jsx";

const RECIPE_TYPES = [
  { value: "liquid_culture", label: "Liquid Culture" },
  { value: "bulk_spawn",     label: "Bulk Spawn" },
  { value: "substrate",      label: "Substrate" },
  { value: "other",          label: "Other" },
];

const TYPE_BADGE_COLOR = {
  liquid_culture: "info",
  bulk_spawn:     "warning",
  substrate:      "emerald",
  other:          "gray",
};

function typeLabel(t) {
  return RECIPE_TYPES.find(r => r.value === t)?.label ?? t;
}

export default function RecipeDetail() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { data: recipe, isLoading, isError } = useRecipeQuery(recipeId);
  const updateRecipe = useUpdateRecipe(recipeId);
  const deleteRecipe = useDeleteRecipe();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [ingredientRows, setIngredientRows] = useState(null);
  const [procedureSteps, setProcedureSteps] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) return <LayoutWide title="Recipe"><Spinner /></LayoutWide>;
  if (isError) return <LayoutWide title="Recipe"><ErrorState>Recipe not found.</ErrorState></LayoutWide>;

  function startEdit() {
    setForm({
      name:        recipe.name,
      recipe_type: recipe.recipe_type,
      notes:       recipe.notes ?? "",
    });
    setIngredientRows(parseIngredients(recipe.ingredients));
    setProcedureSteps(parseSteps(recipe.procedure));
    setSaveError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setForm(null);
    setIngredientRows(null);
    setProcedureSteps(null);
    setSaveError(null);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError("Name is required.");
      return;
    }
    setSaveError(null);
    try {
      await updateRecipe.mutateAsync({
        name:        form.name.trim(),
        recipe_type: form.recipe_type,
        ingredients: serializeIngredients(ingredientRows),
        procedure:   serializeSteps(procedureSteps),
        notes:       form.notes.trim() || null,
      });
      setEditing(false);
      setForm(null);
      setIngredientRows(null);
      setProcedureSteps(null);
    } catch {
      setSaveError("Failed to save. Please try again.");
    }
  }

  async function handleDelete() {
    await deleteRecipe.mutateAsync(recipe.id);
    navigate("/recipes");
  }

  const displayType = editing ? form.recipe_type : recipe.recipe_type;

  return (
    <LayoutWide title={recipe.name}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/recipes")} className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            ← Recipes
          </button>
          {editing ? (
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="text-2xl font-semibold text-sky-800 dark:text-sky-300 border-b-2 border-sky-400 dark:border-sky-600 bg-transparent focus:outline-none"
            />
          ) : (
            <h1 className="text-2xl font-semibold text-sky-800 dark:text-sky-300">{recipe.name}</h1>
          )}
          {editing ? (
            <Select
              size="sm"
              value={form.recipe_type}
              onChange={e => setForm(f => ({ ...f, recipe_type: e.target.value }))}
              className="rounded-pill px-3 py-1 text-xs"
            >
              {RECIPE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          ) : (
            <Badge color={TYPE_BADGE_COLOR[displayType]}>
              {typeLabel(displayType)}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {editing ? (
            <>
              {saveError && <span className="text-xs text-red-600 dark:text-red-400">{saveError}</span>}
              <Button size="sm" onClick={handleSave} disabled={updateRecipe.isPending}>
                {updateRecipe.isPending ? "Saving…" : "Save"}
              </Button>
              <button onClick={cancelEdit} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1.5">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={startEdit}
                className="text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Edit
              </button>
              {confirmDelete ? (
                <span className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  Delete?
                  <button onClick={handleDelete} className="font-semibold hover:underline">Yes</button>
                  <span>/</span>
                  <button onClick={() => setConfirmDelete(false)} className="hover:underline">No</button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="text-sm text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 px-2 py-1.5"
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-5 max-w-2xl">
        <Card variant="outline" padding="none" className="p-6 flex flex-col gap-6">

          {/* Ingredients */}
          <div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Ingredients</div>
            {editing
              ? <IngredientEditor rows={ingredientRows} onChange={setIngredientRows} />
              : <IngredientTable raw={recipe.ingredients} />
            }
          </div>

          {/* Procedure */}
          <div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Procedure</div>
            {editing
              ? <ProcedureEditor steps={procedureSteps} onChange={setProcedureSteps} />
              : <ProcedureSteps raw={recipe.procedure} />
            }
          </div>

          {/* Notes */}
          <div>
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Notes</div>
            {editing ? (
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Any additional notes or tips..."
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-y"
              />
            ) : (
              recipe.notes
                ? <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{recipe.notes}</p>
                : <p className="text-sm text-gray-400 dark:text-gray-500 italic">No notes.</p>
            )}
          </div>

          <div className="text-xs text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-700">
            Created {new Date(recipe.created_at).toLocaleDateString()}
            {recipe.updated_at !== recipe.created_at && (
              <> · Updated {new Date(recipe.updated_at).toLocaleDateString()}</>
            )}
          </div>
        </Card>

        {/* Photos */}
        <PhotoGallery resource="recipes" id={recipe.id} />

        {/* Unit Converter */}
        <UnitConverter />
      </div>
    </LayoutWide>
  );
}
